import * as Sentry from "@sentry/react";
import { createURLWithParams, type ParamsType } from "./create-url-with-paramst";

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export interface IRequestApiError<T> {
  code: number;
  data: T;
}

interface IRequestApiParams {
  path: string;
  method?: RequestMethod;
  headers?: Record<string, string>;
  body?: unknown;
  queryParams?: ParamsType;
  /** false — без провайдера авторизации: запрос авторизуется своими заголовками (так делает обмен токена). */
  withAuth?: boolean;
  responseType?: "blob" | "json" | "text";
  onChunk?: (responseText: string) => void;
  followUpHandler?: (responseText: string[]) => void;
}

interface IProcessStreamParams {
  body: ReadableStream<Uint8Array>;
  onChunk: (responseText: string) => void;
  followUpHandler?: (responseText: string[]) => void;
}

/** initial — первая попытка; retry — повтор после 401: провайдер обязан взять свежий токен, а не кэш. */
export type TAuthAttempt = "initial" | "retry";

/**
 * Авторизация запросов. Провайдера регистрирует user store при старте: shared не знает ни про MSAL,
 * ни про стор и не импортирует service — иначе получился бы циклический импорт.
 */
export interface IRequestAuthProvider {
  getAuthHeaders: (attempt: TAuthAttempt) => Promise<Record<string, string>>;
  /** 401 и на повторе со свежим токеном: сессию считаем истёкшей. */
  onUnauthorized: () => void;
}

let authProvider: IRequestAuthProvider | null = null;

export const setRequestAuthProvider = (provider: IRequestAuthProvider | null) => {
  authProvider = provider;
};

async function apiFetch(path: string, init: RequestInit = {}, queryParams?: ParamsType): Promise<Response> {
  const trimmedPath = path.replace(/^\/+|\/+$/g, "");
  const API_URL = import.meta.env?.VITE_API_URL || window.location.origin;

  const fullPath = trimmedPath.includes("http")
    ? trimmedPath
    : `${API_URL}/api/v1/${trimmedPath.replace("api/v1/", "")}`;

  const url = createURLWithParams({ baseURL: fullPath, params: queryParams });

  return fetch(url, {
    credentials: "omit",
    ...init,
  });
}

async function processStream({ body, onChunk }: IProcessStreamParams): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let completeMessage = "";
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let newlineIndex;

    while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
      const jsonLine = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);

      if (!jsonLine) continue;

      try {
        const chunkParsed = JSON.parse(jsonLine);

        if (chunkParsed?.type === "text" && typeof chunkParsed.content === "string") {
          completeMessage += chunkParsed.content;
          onChunk(completeMessage);
        }
      } catch (error) {
        console.error("Error parsing JSON line:", jsonLine, error);
      }
    }
  }

  return completeMessage;
}

export async function requestApi<T>(params: IRequestApiParams): Promise<T> {
  const {
    path,
    method = "GET",
    headers = {},
    body,
    queryParams,
    withAuth = true,
    responseType = "json",
    onChunk,
    followUpHandler,
  } = params;

  const init: RequestInit = {
    method,
    headers: {
      ...(body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      Accept: "application/json",
      ...headers,
    },
    body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
  };

  const provider = withAuth ? authProvider : null;

  const request = async (attempt: TAuthAttempt): Promise<Response> => {
    const authHeaders = provider ? await provider.getAuthHeaders(attempt) : {};

    return apiFetch(
      path,
      {
        ...init,
        headers: {
          ...init.headers,
          ...authHeaders,
        },
      },
      queryParams,
    );
  };

  try {
    let response = await request("initial");

    // один повтор со свежим токеном; второй 401 — сессия истекла
    if (response.status === 401 && provider) {
      response = await request("retry");

      if (response.status === 401) {
        provider.onUnauthorized();

        throw new CustomHttpError({
          message: "Session expired",
          status: 401,
          url: response.url,
          method,
          data: { detail: "Your session has expired. Please sign in again." },
        });
      }
    }

    if (onChunk && response.body) {
      return (await processStream({
        body: response.body,
        onChunk,
        followUpHandler,
      })) as T;
    }

    if (!response.ok) {
      const errorDetails = await response.json();

      throw new CustomHttpError({
        message: `HTTP error! Status: ${response.status}. Details: ${errorDetails}`,
        status: response.status,
        url: response.url,
        method,
        data: errorDetails,
      });
    }

    const result =
      responseType === "blob"
        ? await response.blob()
        : responseType === "text"
          ? await response.text()
          : await response.json();

    return result;
  } catch (error: any) {
    if (error instanceof CustomHttpError) {
      const errorType =
        error.status && error.status >= 500 ? "http_5xx" : error.status && error.status >= 400 ? "http_4xx" : "network";

      if (error.status !== 401 && error.status !== 403) {
        Sentry.captureException(error, {
          tags: {
            error_type: errorType,
            endpoint: path,
            method: error.method,
            error_domain: "http",
          },
          level: error.status && error.status >= 500 ? "error" : "warning",
          extra: {
            status: error.status,
            url: error.url,
            data: error.data,
            queryParams,
          },
        });
      }
    }

    if (!(error instanceof CustomHttpError)) {
      Sentry.captureException(error, {
        tags: {
          error_type: "network",
          error_domain: "network",
        },
      });
    }

    throw error;
  }
}

export class CustomHttpError extends Error {
  status?: number;
  url: string;
  method: string;
  data?: unknown;

  constructor(params: { message: string; status?: number; url: string; method: string; data?: unknown }) {
    super(params.message);
    this.name = "Http error";
    this.status = params.status;
    this.url = params.url;
    this.method = params.method;
    this.data = params.data;
  }
}
